import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Sparkles, Target, MousePointerClick, Store, Users } from "lucide-react";

const KPI_STATS = [
  {
    title: "Active recommendations",
    value: "12",
    hint: "Currently in market",
    icon: Sparkles,
  },
  {
    title: "Weekly reach (est.)",
    value: "4.2k",
    hint: "Email and in-app combined",
    icon: MousePointerClick,
  },
  {
    title: "Avg. match score",
    value: "87%",
    hint: "Audience alignment index",
    icon: Target,
  },
] as const;

const PLACEMENT_ROWS = [
  {
    id: "1",
    name: "Summer double points",
    type: "Program",
    audience: "Gold & Platinum",
    score: 94,
    channel: "In-app",
    status: "Live",
  },
  {
    id: "2",
    name: "Metro Café — weekend 15%",
    type: "Merchant",
    audience: "Geo: downtown",
    score: 88,
    channel: "SMS",
    status: "Draft",
  },
  {
    id: "3",
    name: "Refer-a-friend bonus",
    type: "Program",
    audience: "All tiers",
    score: 81,
    channel: "Email",
    status: "Live",
  },
  {
    id: "4",
    name: "Electronics partner bundle",
    type: "Merchant",
    audience: "High spend (90d)",
    score: 76,
    channel: "Web",
    status: "Scheduled",
  },
] as const;

const FILTERS = ["All", "Programs", "Merchants", "Top match"] as const;

export default function OffersRecommendation() {
  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm font-medium uppercase tracking-wide text-rose-600">
          Offer Point
        </p>
        <h1 className="text-2xl font-bold text-gray-900">Recommendation</h1>
        <p className="mt-1 max-w-2xl text-gray-600">
          Prioritize offers and placements by audience fit, channel performance, and
          expected lift.
        </p>
      </div>

      <Card className="border-rose-100 shadow-sm bg-gradient-to-br from-white to-rose-50/30">
        <CardContent className="pt-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {KPI_STATS.map((k) => (
              <div
                key={k.title}
                className="rounded-lg border border-rose-100 bg-white/80 p-4"
              >
                <div className="flex items-start justify-between">
                  <p className="text-sm font-medium text-gray-600">{k.title}</p>
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-100 text-rose-700">
                    <k.icon className="h-4 w-4" />
                  </div>
                </div>
                <div className="mt-3 text-2xl font-bold tracking-tight text-gray-900">
                  {k.value}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{k.hint}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-rose-100 shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Suggested placements</CardTitle>
            <CardDescription>
              Ranked by match score and eligibility for the selected audience and
              channels.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <Badge
                key={f}
                variant={f === "All" ? "default" : "outline"}
                className={
                  f === "All"
                    ? "bg-rose-600 hover:bg-rose-600 text-white border-transparent"
                    : "border-rose-200 text-rose-800"
                }
              >
                {f}
              </Badge>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-rose-100 hover:bg-transparent">
                <TableHead className="text-gray-700">Offer</TableHead>
                <TableHead className="text-gray-700">Type</TableHead>
                <TableHead className="text-gray-700">Audience</TableHead>
                <TableHead className="text-gray-700 text-right">Score</TableHead>
                <TableHead className="text-gray-700">Channel</TableHead>
                <TableHead className="text-gray-700">Status</TableHead>
                <TableHead className="text-right text-gray-700">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {PLACEMENT_ROWS.map((row) => (
                <TableRow key={row.id} className="border-rose-50">
                  <TableCell className="font-medium text-gray-900">{row.name}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      {row.type === "Program" ? (
                        <Users className="h-3.5 w-3.5" />
                      ) : (
                        <Store className="h-3.5 w-3.5" />
                      )}
                      {row.type}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-[200px] truncate">
                    {row.audience}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium text-rose-700">
                    {row.score}%
                  </TableCell>
                  <TableCell className="text-muted-foreground">{row.channel}</TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      className={
                        row.status === "Live"
                          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                          : row.status === "Draft"
                            ? "bg-amber-50 text-amber-900 border-amber-200"
                            : "bg-sky-50 text-sky-900 border-sky-200"
                      }
                    >
                      {row.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="text-rose-700 h-8">
                      Preview
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
